"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.clearedOnboardingAssistantSeed = exports.requestedOnboardingAssistant = exports.visitedOnboardingDashboard = exports.clearedOnboardingStep = exports.startedOnboardingTour = exports.startedOnboardingStep = exports.finishedOnboarding = exports.dismissedOnboarding = void 0;
const initial_1 = __importDefault(require("@app/store/initial"));
const toolkit_1 = require("@reduxjs/toolkit");
const action_type_1 = __importDefault(require("../const/action-type"));
/**
 * Which workspaces the manager has skipped or finished the onboarding checklist in.
 * Keyed by workspace id, because one account can own several workspaces and each of them
 * is set up separately.
 *
 * Lives in Redux (rather than plain localStorage) so that skipping re-renders the nav and
 * the router in the same tick; it is persisted through the store whitelist in
 * `app/store/persist.js`. Deliberately does NOT reset on `RESET_STORE` — a manager who
 * skipped the checklist and then logged out must not be dropped back onto it on the next
 * sign-in. `creationMetadata` would have been the server-side home for this, but
 * `WorkspaceInputData` has no `creationMetadata` field, so it is only writable at
 * workspace-creation time (see PLAN.md phase 6).
 */
const onboardingSlice = (0, toolkit_1.createSlice)({
    name: 'onboarding',
    initialState: initial_1.default.onboarding,
    reducers: {
        // „Přeskočit nastavení" — stop landing on the checklist, but keep it reachable from
        // the nav so the manager can come back and finish.
        dismissedOnboarding: (state, action) => {
            state[action.payload] = { ...state[action.payload], dismissed: true };
        },
        // The congratulations screen was acknowledged: the checklist is done with, and both
        // the route and the nav item disappear for good.
        finishedOnboarding: (state, action) => {
            state[action.payload] = { dismissed: true, hidden: true };
        },
        // „Vyřešit" was clicked: the manager left the checklist to do this one step somewhere
        // else in the app. Remembered here (and therefore persisted) because the work happens
        // on other pages and can easily span a reload — see `use-onboarding-step-toast`, which
        // reads it on the way back to turn it into the „step completed" toast.
        startedOnboardingStep: (state, action) => {
            state[action.payload.workspaceId] = {
                ...state[action.payload.workspaceId],
                activeStep: action.payload.step,
                // this click, and only this click, entitles the step to open its Intercom tour on
                // the page it navigates to — see OnboardingTourLauncher
                tourPending: true,
                // which steps were ALREADY finished at that moment, so that whatever is done by the
                // time the manager comes back can be told apart from what they just did — one
                // action often finishes two steps at once (publishing a schedule creates one and
                // publishes it), and the toast has to animate both, not one of them.
                doneStepsAtStart: action.payload.doneSteps,
            };
        },
        // „Vyřešit" also opens the manager assistant on a fresh thread about that step. The
        // request is parked here (rather than in the managerAssistant slice) because it is
        // onboarding state; the chat lives in a different subtree and consumes it on the next
        // render. Every click writes it again — including a repeat click on the same step —
        // which is what makes „a new thread per click" work, since the chat clears it on
        // consume.
        requestedOnboardingAssistant: (state, action) => {
            state[action.payload.workspaceId] = {
                ...state[action.payload.workspaceId],
                assistantSeedStep: action.payload.step,
            };
        },
        // The chat has seeded the thread. Clearing is what keeps the request one-shot: this
        // slice is persisted, so a seed left behind would fire again on the next reload.
        clearedOnboardingAssistantSeed: (state, action) => {
            state[action.payload] = {
                ...state[action.payload],
                assistantSeedStep: undefined,
            };
        },
        // The step's tour has been handed to Intercom. Clearing this is what stops the tour
        // from opening again every time the manager walks back onto the same page — the step
        // itself stays active until it is actually finished, and clicking „Vyřešit" again is
        // what asks for a replay.
        startedOnboardingTour: (state, action) => {
            state[action.payload] = { ...state[action.payload], tourPending: false };
        },
        // The manager opened the manager dashboard. Every other step reads its „done" out of
        // workspace data, but „look at your dashboard" leaves no trace on the server — there is
        // nothing to create and nothing to count — so the visit itself is recorded here.
        // Persisted like the rest of the slice, so the step stays done after a reload.
        visitedOnboardingDashboard: (state, action) => {
            state[action.payload] = { ...state[action.payload], visitedDashboard: true };
        },
        // The tracked step was detected as done (or the manager started another one), so there
        // is nothing left to watch for.
        clearedOnboardingStep: (state, action) => {
            state[action.payload] = {
                ...state[action.payload],
                activeStep: undefined,
                doneStepsAtStart: undefined,
                tourPending: false,
            };
        },
    },
    // Only the two session-scoped flags are reset here — never `dismissed`/`hidden`, for the
    // reason in the comment above the slice.
    //
    // `assistantSeedStep`: a seed the chat never got to consume belongs to the workspace and
    // the moment it was clicked in, so a switch (or a store reset) must not leave it waiting
    // to open a thread nobody asked for.
    //
    // `tourPending`: it is deliberately not persisted (see `app/store/persist.js`), but the
    // slice survives a store reset, so without this a logout followed by a sign-in in the
    // same session would keep the latch set while `activeStep` comes back from storage — and
    // the next navigation onto that step's page would open a tour nobody asked for.
    extraReducers: (builder) => {
        const clearSessionFlags = (state) => {
            Object.keys(state).forEach((workspaceId) => {
                state[workspaceId] = {
                    ...state[workspaceId],
                    assistantSeedStep: undefined,
                    tourPending: undefined,
                };
            });
        };
        builder
            .addCase(action_type_1.default.SWITCH_WORKSPACE, clearSessionFlags)
            .addCase(action_type_1.default.RESET_STORE, clearSessionFlags);
    },
});
_a = onboardingSlice.actions, exports.dismissedOnboarding = _a.dismissedOnboarding, exports.finishedOnboarding = _a.finishedOnboarding, exports.startedOnboardingStep = _a.startedOnboardingStep, exports.startedOnboardingTour = _a.startedOnboardingTour, exports.clearedOnboardingStep = _a.clearedOnboardingStep, exports.visitedOnboardingDashboard = _a.visitedOnboardingDashboard, exports.requestedOnboardingAssistant = _a.requestedOnboardingAssistant, exports.clearedOnboardingAssistantSeed = _a.clearedOnboardingAssistantSeed;
exports.default = onboardingSlice.reducer;
